import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, TextInput, Label, Spinner, Modal } from "flowbite-react";
import {
  HiFingerPrint,
  HiPlus,
  HiTrash,
  HiPencil,
  HiCheck,
  HiX,
} from "react-icons/hi";

import { usePasskeys, useToast } from "../../../hooks";
import { NOTIFICATION_TYPES } from "../../../utils";

export const SecuritySettings = () => {
  const { t } = useTranslation();
  const {
    registerPasskey,
    listPasskeys,
    renamePasskey,
    deletePasskey,
    isSupported,
    loading,
  } = usePasskeys();
  const showToast = useToast();

  const [passkeyName, setPasskeyName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  const fetchPasskeys = async () => {
    setLoadingPasskeys(true);
    const result = await listPasskeys();
    if (result.success) {
      setPasskeys(result.passkeys);
    }
    setLoadingPasskeys(false);
  };

  useEffect(() => {
    if (isSupported()) {
      fetchPasskeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartRegister = () => {
    if (!passkeyName.trim()) {
      showToast({
        content: t("pages.settings.security.passkeys.enterName"),
        type: NOTIFICATION_TYPES.error,
      });
      return;
    }
    setShowPasswordModal(true);
  };

  const handleConfirmRegister = async () => {
    if (!password) {
      setPasswordError(
        t("pages.settings.security.confirmIdentity.enterPassword"),
      );
      return;
    }

    setPasswordError("");
    setVerifyingPassword(true);

    const result = await registerPasskey(passkeyName.trim(), password);

    setVerifyingPassword(false);

    if (result.success) {
      showToast({
        content: t("pages.settings.security.passkeys.registerSuccess"),
        type: NOTIFICATION_TYPES.success,
      });
      setPasskeyName("");
      setPassword("");
      setShowRegister(false);
      setShowPasswordModal(false);
      fetchPasskeys();
    } else if (result.error === "Incorrect password") {
      // Show password error in modal
      setPasswordError(
        t("pages.settings.security.confirmIdentity.incorrectPassword"),
      );
    } else if (result.error === "Passkey registration was cancelled") {
      // User cancelled WebAuthn - just close modal, no error
      setShowPasswordModal(false);
      setPassword("");
    } else {
      // Other errors - show in modal
      setPasswordError(result.error);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPassword("");
    setPasswordError("");
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;

    const result = await renamePasskey(id, editName.trim());
    if (result.success) {
      showToast({
        content: t("pages.settings.security.passkeys.renamed"),
        type: NOTIFICATION_TYPES.success,
      });
      setEditingId(null);
      fetchPasskeys();
    } else {
      showToast({
        content: result.error,
        type: NOTIFICATION_TYPES.error,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("pages.settings.security.passkeys.confirmDelete"))) {
      return;
    }

    const result = await deletePasskey(id);
    if (result.success) {
      showToast({
        content: t("pages.settings.security.passkeys.deleted"),
        type: NOTIFICATION_TYPES.success,
      });
      fetchPasskeys();
    } else {
      showToast({
        content: result.error,
        type: NOTIFICATION_TYPES.error,
      });
    }
  };

  if (!isSupported()) {
    return (
      <div className="max-w-2xl">
        <h2 className="mb-4 text-xl font-semibold">
          {t("pages.settings.security.title")}
        </h2>
        <Card>
          <div className="text-gray-500">
            {t("pages.settings.security.passkeys.notSupported")}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h2 className="mb-4 text-xl font-semibold">
        {t("pages.settings.security.title")}
      </h2>

      <Card className="mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t("pages.settings.security.passkeys.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("pages.settings.security.passkeys.description")}
            </p>
          </div>
          <HiFingerPrint className="h-8 w-8 text-blue-600" />
        </div>

        {loadingPasskeys ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            {passkeys.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  {t("pages.settings.security.passkeys.yourPasskeys")}
                </h4>
                {passkeys.map((passkey) => (
                  <div
                    key={passkey.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
                  >
                    {editingId === passkey.id ? (
                      <div className="flex flex-1 items-center gap-2">
                        <TextInput
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1"
                          sizing="sm"
                        />
                        <Button
                          size="xs"
                          color="blue"
                          onClick={() => handleRename(passkey.id)}
                        >
                          <HiCheck className="h-4 w-4" />
                        </Button>
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => setEditingId(null)}
                        >
                          <HiX className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="font-medium">{passkey.name}</div>
                          <div className="text-sm text-gray-500">
                            {t("pages.settings.security.passkeys.added")}{" "}
                            {new Date(passkey.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            color="gray"
                            outline
                            onClick={() => {
                              setEditingId(passkey.id);
                              setEditName(passkey.name);
                            }}
                          >
                            <HiPencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            color="failure"
                            outline
                            onClick={() => handleDelete(passkey.id)}
                          >
                            <HiTrash className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showRegister ? (
              <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div>
                  <Label
                    htmlFor="passkey-name"
                    value={t("pages.settings.security.passkeys.passkeyName")}
                  />
                  <TextInput
                    id="passkey-name"
                    placeholder={t(
                      "pages.settings.security.passkeys.passkeyNamePlaceholder",
                    )}
                    value={passkeyName}
                    onChange={(e) => setPasskeyName(e.target.value)}
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("pages.settings.security.passkeys.passkeyNameHint")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button color="blue" onClick={handleStartRegister}>
                    {t("pages.settings.security.passkeys.registerPasskey")}
                  </Button>
                  <Button
                    color="gray"
                    onClick={() => {
                      setShowRegister(false);
                      setPasskeyName("");
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                color="blue"
                outline
                className="mt-4"
                onClick={() => setShowRegister(true)}
              >
                <HiPlus className="mr-2 h-4 w-4" />
                {t("pages.settings.security.passkeys.addPasskey")}
              </Button>
            )}
          </>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900">
          {t("pages.settings.security.about.title")}
        </h3>
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-gray-600">
          <li>{t("pages.settings.security.about.storedSecurely")}</li>
          <li>{t("pages.settings.security.about.syncAcrossDevices")}</li>
          <li>{t("pages.settings.security.about.noPasswords")}</li>
          <li>{t("pages.settings.security.about.phishingProtection")}</li>
          <li>{t("pages.settings.security.about.fallbackLogin")}</li>
        </ul>
      </Card>

      {/* Password confirmation modal */}
      <Modal
        show={showPasswordModal}
        onClose={handleClosePasswordModal}
        size="md"
      >
        <Modal.Header>
          {t("pages.settings.security.confirmIdentity.title")}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              {t("pages.settings.security.confirmIdentity.description")}
            </p>
            <div>
              <Label
                htmlFor="confirm-password"
                value={t("pages.settings.security.confirmIdentity.password")}
              />
              <TextInput
                id="confirm-password"
                type="password"
                placeholder={t(
                  "pages.settings.security.confirmIdentity.passwordPlaceholder",
                )}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !verifyingPassword) {
                    handleConfirmRegister();
                  }
                }}
                className="mt-1"
                color={passwordError ? "failure" : undefined}
                helperText={
                  passwordError && (
                    <span className="text-red-600">{passwordError}</span>
                  )
                }
                autoFocus
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="blue"
            onClick={handleConfirmRegister}
            isProcessing={verifyingPassword || loading}
          >
            {t("pages.settings.security.confirmIdentity.confirm")}
          </Button>
          <Button color="gray" onClick={handleClosePasswordModal}>
            {t("common.cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
